import React, { useEffect, useState, useMemo } from 'react';
import {
  Typography,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Box,
  TableSortLabel,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  Tab,
  Tabs,
  Chip,
  Checkbox,
} from '@mui/material';
import { LineChart, Line } from 'recharts';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard } from '../components/layout/EnhancedCard';
import DivineOrbIcon from '../components/icons/DivineOrbIcon';
import StopIcon from '@mui/icons-material/Stop';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch, useSelector } from 'react-redux';
import { computePercentile, getPercentileArrow, getPercentileColor } from '../utils/percentile';
import type { AppDispatch } from '../store/store';
import GameLeagueSelector from '../components/GameLeagueSelector';
import { selectGameFilter, selectLeagueFilter } from '../store/slices/filterSlice';
import { selectUserFeatures } from '../store/slices/authSlice';
import { selectUserRoles } from '../store/slices/authSlice';
import {
  fetchCharacters,
  selectAllCharacters,
  selectCharactersStatus,
  selectCharactersError,
  clearCharactersError,
  fetchCharacterDeaths,
  fetchBulkIsAlive,
  fetchBulkLastInventoryUpdate,
  selectAliveStatuses,
  selectLastInventoryUpdateTimestamps,
  bulkDeleteCharacters,
  //selectAliveStatus,
} from '../store/slices/charactersSlice';
import {
  fetchCharacterItems,
  fetchDivineOrbHistory,
  fetchDivineOrbsPerHour,
  fetchBulkDivineOrbData,
  selectCharacterItems,
  selectCharacterItemsStatus,
  selectDivineOrbHistory,
  selectDivineOrbsPerHour,
  selectDivineOrbsGained24h,
} from '../store/slices/itemsSlice';
import CharacterPanel from '../components/character/CharacterPanel';
import CharacterStatsDashboard from '../components/character/CharacterStatsDashboard';
import BuildAnalysisView from '../components/character/BuildAnalysisView';
import BulkDeleteConfirmationDialog from '../components/character/BulkDeleteConfirmationDialog';
import BulkEditNoteModal from '../components/character/BulkEditNoteModal';
import NoteTemplatesAdminModal from '../components/character/NoteTemplatesAdminModal';
import { queueCommands } from '../services/commandsService';
import { updateCharacterNote, updateCharacterBoostingState } from '../services/characterService';
import { showNotification } from '../store/slices/notificationSlice';
import PeopleIcon from '@mui/icons-material/People';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { getStateColorConfig, parseState } from '../utils/stateColorMap';

const CharacterManagementPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const characters = useSelector(selectAllCharacters);
  const status = useSelector(selectCharactersStatus);
  const error = useSelector(selectCharactersError);
  const userFeatures = useSelector(selectUserFeatures);
  const aliveStatuses = useSelector(selectAliveStatuses);
  const lastInventoryUpdateTimestamps = useSelector(selectLastInventoryUpdateTimestamps);
  //const aliveStatus = useSelector(selectAliveStatus);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Sorting state
  type Order = 'asc' | 'desc';
  type OrderBy = 'divineOrbCount' | 'divPerHour' | 'divsGained24h' | 'deathsPerHour' | 'hostname' | 'level' | 'name';
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('divineOrbCount');

  // Character panel state
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState<null | any>(null);

  // Loading states for the new buttons
  const [stopping, setStopping] = useState<Record<string, boolean>>({});
  const [reloadingAll, setReloadingAll] = useState(false);

  // Note editing state
  const [editingNote, setEditingNote] = useState<Record<string, boolean>>({});
  const [noteValues, setNoteValues] = useState<Record<string, string>>({});
  const [savingNote, setSavingNote] = useState<Record<string, boolean>>({});

  // State for periodic data refresh
  const [isPeriodicRefreshing, setIsPeriodicRefreshing] = useState(false);

  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Selection + bulk note state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [manageTemplatesOpen, setManageTemplatesOpen] = useState(false);

  const isOwnerOrAdmin = useSelector(selectUserRoles).some(r => r === 'owner' || r === 'admin');

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = (checked: boolean) => {
    setSelectedIds(prev => {
      if (!checked) return new Set();
      const next = new Set(prev);
      for (const c of characters) next.add(c.id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Prune selection when character list changes
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(characters.map(c => c.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev)
        if (visible.has(id)) next.add(id);
        else changed = true;
      return changed ? next : prev;
    });
  }, [characters]);
  // RMT modal state
  const [rmtModalOpen, setRmtModalOpen] = useState(false);
  const [rmtCharacterId, setRmtCharacterId] = useState<string | null>(null);
  const [rmtDivAmount, setRmtDivAmount] = useState<string>('');
  const [rmtError, setRmtError] = useState<string>('');
  const [rmtSubmitting, setRmtSubmitting] = useState(false);

  // Character items state from Redux
  const characterItems = useSelector(selectCharacterItems);
  const characterItemsStatus = useSelector(selectCharacterItemsStatus);
  //const characterItemsError = useSelector(selectCharacterItemsError);

  // Divine orb data from Redux
  const divineOrbHistory = useSelector(selectDivineOrbHistory);
  const divineOrbsPerHour = useSelector(selectDivineOrbsPerHour);
  const divineOrbsGained24h = useSelector(selectDivineOrbsGained24h);

  // Get selected game and league from filter state
  const selectedGame = useSelector(selectGameFilter);
  const selectedLeague = useSelector(selectLeagueFilter);

  // Utility function to format time ago
  const formatTimeAgo = (isoString: string): string => {
    const now = Date.now();
    const then = new Date(isoString).getTime();
    const diffMs = now - then;

    if (diffMs < 60_000) return `${Math.floor(diffMs / 1000)}s`;
    if (diffMs < 3_600_000) return `${Math.floor(diffMs / 60_000)}m`;
    if (diffMs < 86_400_000) {
      const hours = Math.floor(diffMs / 3_600_000);
      const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    return `${Math.floor(diffMs / 86_400_000)}d`;
  };

  useEffect(() => {
    // Clear any previous errors and fetch characters when filters change
    dispatch(clearCharactersError()); // Clear existing errors
    dispatch(fetchCharacters())
      .unwrap() // unwrap to handle promise result
      .then(fetchedCharacters => {
        if (fetchedCharacters && fetchedCharacters.length > 0) {
          // Use bulk endpoint to fetch divine orb data for all characters at once
          const characterIds = fetchedCharacters.filter(character => character && character.id).map(character => character.id);

          if (characterIds.length > 0) {
            dispatch(fetchBulkDivineOrbData(characterIds));
          }

          if (characterIds.length > 0) {
            dispatch(fetchCharacterDeaths({ characterIds, hours: 4 }));
            dispatch(fetchBulkIsAlive({ characterIds }));
            dispatch(fetchBulkLastInventoryUpdate({ characterIds }));
          }
        }
      })
      .catch(err => {
        // Handle potential errors from fetchCharacters if needed,
        // though the slice already handles setting error state.
        console.error('Error fetching characters or subsequent data:', err);
      });

    // Optional: Clear error when component unmounts
    return () => {
      // dispatch(clearCharactersError()); // Example: clear error on unmount
    };
  }, [dispatch, selectedGame, selectedLeague]); // Re-fetch when filters change

  // Effect to clear error on component mount if it's stale from a previous view
  useEffect(() => {
    if (error) {
      dispatch(clearCharactersError());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // Run only once on mount if error exists

  // Effect to sync selectedCharacter with updated character from Redux store
  useEffect(() => {
    if (selectedCharacter && characters.length > 0) {
      const updatedCharacter = characters.find(char => char.id === selectedCharacter.id);
      if (updatedCharacter && updatedCharacter !== selectedCharacter) {
        setSelectedCharacter(updatedCharacter);
      }
    }
  }, [characters, selectedCharacter]);

  // Effect for 1-minute timer to update "Last Alive" data from API
  useEffect(() => {
    const updateLastAlive = async () => {
      if (characters.length === 0) return;

      try {
        const characterIds = characters.filter(character => character && character.id).map(character => character.id);
        if (characterIds.length > 0) {
          await dispatch(fetchBulkIsAlive({ characterIds }));
        }
      } catch (error) {
        console.error('Error updating Last Alive data:', error);
      }
    };

    const interval = setInterval(updateLastAlive, 60000); // 1 minute

    return () => clearInterval(interval);
  }, [dispatch, characters]);

  // Effect for 2-minute periodic data refresh
  useEffect(() => {
    const refreshData = async () => {
      if (characters.length === 0) return;

      setIsPeriodicRefreshing(true);
      try {
        const characterIds = characters.filter(character => character && character.id).map(character => character.id);

        if (characterIds.length > 0) {
          // Refresh all character data
          await Promise.all([
            dispatch(fetchBulkDivineOrbData(characterIds)),
            dispatch(fetchCharacterDeaths({ characterIds, hours: 4 })),
            dispatch(fetchBulkIsAlive({ characterIds })),
            dispatch(fetchBulkLastInventoryUpdate({ characterIds })),
          ]);
        }
      } catch (error) {
        console.error('Error during periodic data refresh:', error);
      } finally {
        setIsPeriodicRefreshing(false);
      }
    };

    // Set up the interval for 2-minute refresh
    const interval = setInterval(refreshData, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, [dispatch, characters, selectedGame, selectedLeague]);

  // Handle sort request
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort characters based on current sort mode
  const sortedCharacters = useMemo(() => {
    if (characters.length === 0) return characters;

    const stabilizedThis = characters.map((el, index) => [el, index] as [any, number]);

    stabilizedThis.sort((a, b) => {
      let orderVal = 0;
      const getVal = (char: any, currentOrderBy: OrderBy) => {
        switch (currentOrderBy) {
          case 'divPerHour':
            return divineOrbsPerHour[char.id]?.divPerHour ?? 0;
          case 'divsGained24h':
            return divineOrbsGained24h[char.id]?.divineOrbsGained ?? 0;
          case 'deathsPerHour':
            return char.deathsPerHour ?? 0;
          case 'hostname':
            return char.hostname?.toLowerCase() ?? 'zzzzzzzz';
          case 'name':
            return (char.name || '').toLowerCase();
          case 'level':
            return char.level ?? 0;
          case 'divineOrbCount':
          default:
            return char.divineOrbCount ?? 0;
        }
      };

      const valA = getVal(a[0], orderBy);
      const valB = getVal(b[0], orderBy);

      if (valA < valB) {
        orderVal = -1;
      } else if (valA > valB) {
        orderVal = 1;
      }

      if (orderVal !== 0) return order === 'asc' ? orderVal : -orderVal;

      return a[1] - b[1]; // stable sort
    });

    return stabilizedThis.map(el => el[0]);
  }, [characters, order, orderBy, divineOrbsPerHour, divineOrbsGained24h]);

  // Memoized percentile distribution arrays for performance
  const allDivPerHourValues = useMemo(
    () =>
      Object.values(divineOrbsPerHour)
        .map(data => data.divPerHour)
        .filter(rate => rate > 0),
    [divineOrbsPerHour],
  );

  const allDivsGained24hValues = useMemo(
    () =>
      Object.values(divineOrbsGained24h)
        .map(data => data.divineOrbsGained)
        .filter(gain => gain > 0),
    [divineOrbsGained24h],
  );

  // Handler for character selection
  const handleCharacterClick = (character: any) => {
    setSelectedCharacter(character);
    setIsPanelOpen(true);

    // Fetch character items and divine orb data when a character is selected
    if (character && character.id) {
      dispatch(fetchCharacterItems(character.id));
      // Individual data fetching only for the selected character
      // No need to fetch these again if they were already loaded via bulk request
      if (!divineOrbHistory[character.id] || !divineOrbsPerHour[character.id]) {
        dispatch(fetchDivineOrbHistory(character.id));
        dispatch(fetchDivineOrbsPerHour(character.id));
      }
    }
  };

  // Handler for closing the panel
  const handleClosePanel = () => {
    setIsPanelOpen(false);
  };

  // Handler for stopping a character in hideout
  const handleStopInHideout = async (e: React.MouseEvent, characterId: string) => {
    e.stopPropagation(); // Prevent row click
    setStopping(prev => ({ ...prev, [characterId]: true }));
    try {
      await queueCommands([characterId], 'stop_in_hideout');
      // Optional: show success notification
    } catch (err) {
      console.error('Failed to queue stop_in_hideout:', err);
      // Optional: show error notification
    } finally {
      setStopping(prev => ({ ...prev, [characterId]: false }));
    }
  };

  // Handler for reloading all scripts
  const handleReloadAllScripts = async () => {
    if (reloadingAll || characters.length === 0) return;
    setReloadingAll(true);
    try {
      const allCharacterIds = characters.map(c => c.id);
      await queueCommands(allCharacterIds, 'reload_scripts');
      // Optional: show success notification
    } catch (err) {
      console.error('Failed to queue reload_scripts:', err);
      // Optional: show error notification
    } finally {
      setReloadingAll(false);
    }
  };

  // Note editing handlers
  const handleEditNote = (characterId: string, currentNote: string) => {
    setEditingNote(prev => ({ ...prev, [characterId]: true }));
    setNoteValues(prev => ({ ...prev, [characterId]: currentNote || '' }));
  };

  const handleCancelEditNote = (characterId: string) => {
    setEditingNote(prev => ({ ...prev, [characterId]: false }));
    setNoteValues(prev => ({ ...prev, [characterId]: '' }));
  };

  const handleSaveNote = async (characterId: string) => {
    setSavingNote(prev => ({ ...prev, [characterId]: true }));
    try {
      const note = noteValues[characterId] || '';
      await updateCharacterNote(characterId, note);

      // Update the character in the Redux store
      dispatch(fetchCharacters());

      setEditingNote(prev => ({ ...prev, [characterId]: false }));
      setNoteValues(prev => ({ ...prev, [characterId]: '' }));
    } catch (error) {
      console.error('Failed to update note:', error);
      // Optional: show error notification
    } finally {
      setSavingNote(prev => ({ ...prev, [characterId]: false }));
    }
  };

  // Boosting state handler
  const handleBoostingStateChange = async (characterId: string, newState: number) => {
    try {
      await updateCharacterBoostingState(characterId, newState);
      // Refresh characters to get the updated boosting state
      await dispatch(fetchCharacters());
      dispatch(
        showNotification({
          message: 'Boosting state updated successfully',
          type: 'success',
        }),
      );
    } catch (error) {
      console.error('Failed to update boosting state:', error);
      dispatch(
        showNotification({
          message: 'Failed to update boosting state',
          type: 'error',
        }),
      );
    }
  };

  // RMT handlers
  const handleRmtClick = (e: React.MouseEvent, characterId: string) => {
    e.stopPropagation(); // Prevent row click
    setRmtCharacterId(characterId);
    setRmtDivAmount('');
    setRmtError('');
    setRmtModalOpen(true);
  };

  const handleRmtClose = () => {
    setRmtModalOpen(false);
    setRmtCharacterId(null);
    setRmtDivAmount('');
    setRmtError('');
  };

  const handleRmtSubmit = async () => {
    if (!rmtCharacterId) return;

    // Find the character to get their div balance
    const character = characters.find(c => c.id === rmtCharacterId);
    if (!character) {
      setRmtError('Character not found');
      return;
    }

    // Validate the input
    const divAmount = parseFloat(rmtDivAmount);
    if (isNaN(divAmount) || divAmount <= 0) {
      setRmtError('Please enter a valid positive number');
      return;
    }

    const characterDivBalance = character.divineOrbCount ?? 0;
    if (divAmount > characterDivBalance) {
      setRmtError(`Amount exceeds character's balance (${characterDivBalance} div)`);
      return;
    }

    setRmtSubmitting(true);
    setRmtError('');

    try {
      await queueCommands([rmtCharacterId], 'withdraw_div', { divCount: divAmount });
      dispatch(
        showNotification({
          message: `Successfully queued withdrawal of ${divAmount} div for ${character.name}`,
          type: 'success',
        }),
      );
      handleRmtClose();
    } catch (err: any) {
      console.error('Failed to queue withdraw_div:', err);
      setRmtError(err.response?.data?.message || 'Failed to queue withdrawal command');
    } finally {
      setRmtSubmitting(false);
    }
  };

  const handleNoteChange = (characterId: string, value: string) => {
    setNoteValues(prev => ({ ...prev, [characterId]: value }));
  };

  // Copy character info to clipboard
  const handleCopyCharacterInfo = async (e: React.MouseEvent, character: any) => {
    e.stopPropagation(); // Prevent row click

    const accountName = character.gameAccount?.accountName || 'Unknown';
    const characterName = character.name || 'Unknown';
    const hostname = character.hostname || 'Unknown';

    const copyText = `${accountName} ${characterName} ${hostname}`;

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(copyText);
      } else {
        // Fallback for insecure contexts (HTTP)
        const textArea = document.createElement('textarea');
        textArea.value = copyText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      dispatch(
        showNotification({
          message: 'Character info copied to clipboard!',
          type: 'success',
        }),
      );
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      dispatch(
        showNotification({
          message: 'Failed to copy to clipboard',
          type: 'error',
        }),
      );
    }
  };

  // Bulk delete handlers
  const handleDeleteAllClick = () => {
    if (characters.length === 0) {
      dispatch(
        showNotification({
          message: 'No characters to delete.',
          type: 'info',
        }),
      );
      return;
    }

    // Additional validation
    if (status === 'loading') {
      dispatch(
        showNotification({
          message: 'Please wait for characters to finish loading.',
          type: 'warning',
        }),
      );
      return;
    }

    if (isBulkDeleting) {
      dispatch(
        showNotification({
          message: 'Bulk deletion is already in progress.',
          type: 'warning',
        }),
      );
      return;
    }

    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (characters.length === 0) {
      dispatch(
        showNotification({
          message: 'No characters to delete.',
          type: 'warning',
        }),
      );
      return;
    }

    // Additional validation before deletion
    if (isBulkDeleting) {
      dispatch(
        showNotification({
          message: 'Deletion is already in progress.',
          type: 'warning',
        }),
      );
      return;
    }

    setIsBulkDeleting(true);
    try {
      const characterIds = characters.map(char => char.id);

      // Validate character IDs
      const invalidIds = characterIds.filter(id => !id || typeof id !== 'string');
      if (invalidIds.length > 0) {
        throw new Error('Some characters have invalid IDs and cannot be deleted.');
      }

      const result = await dispatch(bulkDeleteCharacters(characterIds)).unwrap();

      setBulkDeleteDialogOpen(false);

      // Show success notification with details
      if (result.deletedCount === 0) {
        dispatch(
          showNotification({
            message: 'No characters were deleted. Please check the error messages.',
            type: 'warning',
          }),
        );
      } else {
        let message = `Successfully deleted ${result.deletedCount} character${result.deletedCount !== 1 ? 's' : ''}.`;
        if (result.errors.length > 0) {
          message += ` ${result.errors.length} error${result.errors.length !== 1 ? 's' : ''} occurred.`;
        }

        dispatch(
          showNotification({
            message,
            type: result.errors.length > 0 ? 'warning' : 'success',
          }),
        );
      }

      // Show errors if any (limit to first 5 to avoid spam)
      if (result.errors.length > 0) {
        const errorsToShow = result.errors.slice(0, 5);
        errorsToShow.forEach(error => {
          dispatch(
            showNotification({
              message: error,
              type: 'error',
            }),
          );
        });

        if (result.errors.length > 5) {
          dispatch(
            showNotification({
              message: `... and ${result.errors.length - 5} more errors occurred.`,
              type: 'error',
            }),
          );
        }
      }
    } catch (error: unknown) {
      console.error('Failed to bulk delete characters:', error);

      let errorMessage = 'Failed to delete characters. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      dispatch(
        showNotification({
          message: errorMessage,
          type: 'error',
        }),
      );
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkDeleteCancel = () => {
    if (!isBulkDeleting) {
      setBulkDeleteDialogOpen(false);
    }
  };

  let content;

  if (status === 'loading') {
    content = (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (status === 'succeeded') {
    if (characters.length === 0) {
      content = <Alert severity='info'>No characters found.</Alert>;
    } else {
      content = (
        <EnhancedCard sx={{ mt: 3 }}>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label='character table'>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      indeterminate={selectedIds.size > 0 && selectedIds.size < characters.length}
                      checked={characters.length > 0 && selectedIds.size === characters.length}
                      onChange={e => toggleAllVisible(e.target.checked)}
                      inputProps={{ 'aria-label': 'select all characters' }}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', borderRight: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        Name
                      </TableSortLabel>
                      <Box sx={{ borderRight: '1px solid', borderColor: 'divider', height: 16, mx: 0.5 }} />
                      <TableSortLabel
                        active={orderBy === 'hostname'}
                        direction={orderBy === 'hostname' ? order : 'asc'}
                        onClick={() => handleRequestSort('hostname')}
                      >
                        Host
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', width: 65, borderRight: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <TableSortLabel
                        active={orderBy === 'level'}
                        direction={orderBy === 'level' ? order : 'asc'}
                        onClick={() => handleRequestSort('level')}
                      >
                        Level
                      </TableSortLabel>
                      <Box sx={{ borderRight: '1px solid', borderColor: 'divider', height: 16, mx: 0.5 }} />
                      <TableSortLabel
                        active={orderBy === 'deathsPerHour'}
                        direction={orderBy === 'deathsPerHour' ? order : 'asc'}
                        onClick={() => handleRequestSort('deathsPerHour')}
                      >
                        D/hr
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', width: 130, borderRight: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <TableSortLabel
                        active={orderBy === 'divineOrbCount'}
                        direction={orderBy === 'divineOrbCount' ? order : 'asc'}
                        onClick={() => handleRequestSort('divineOrbCount')}
                      >
                        Divs
                      </TableSortLabel>
                      <Box sx={{ borderRight: '1px solid', borderColor: 'divider', height: 16, mx: 0.5 }} />
                      <TableSortLabel
                        active={orderBy === 'divPerHour'}
                        direction={orderBy === 'divPerHour' ? order : 'asc'}
                        onClick={() => handleRequestSort('divPerHour')}
                      >
                        /Hr
                      </TableSortLabel>
                      <Box sx={{ borderRight: '1px solid', borderColor: 'divider', height: 16, mx: 0.5 }} />
                      <TableSortLabel
                        active={orderBy === 'divsGained24h'}
                        direction={orderBy === 'divsGained24h' ? order : 'asc'}
                        onClick={() => handleRequestSort('divsGained24h')}
                      >
                        24h
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider' }}>Build / Strategy</TableCell>
                  <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider' }}>State</TableCell>
                  <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider' }}>Note</TableCell>
                  <TableCell align='right'>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCharacters.map(character => {
                  // Compute percentile values for this character
                  const perHourData = divineOrbsPerHour[character.id];
                  const divPerHour = perHourData ? perHourData.divPerHour : 0;
                  const divPerHourPct =
                    divPerHour > 0 && allDivPerHourValues.length > 0 ? computePercentile(allDivPerHourValues, divPerHour) : -1;
                  const divPerHourColor = divPerHourPct >= 0 ? getPercentileColor(divPerHourPct) : 'text.secondary';
                  const divPerHourArrow = divPerHourPct >= 0 ? getPercentileArrow(divPerHourPct) : '';

                  const gained24hData = divineOrbsGained24h[character.id];
                  const divsGained24h = gained24hData ? gained24hData.divineOrbsGained : 0;
                  const divsGained24hPct =
                    divsGained24h > 0 && allDivsGained24hValues.length > 0 ? computePercentile(allDivsGained24hValues, divsGained24h) : -1;
                  const divsGained24hColor = divsGained24hPct >= 0 ? getPercentileColor(divsGained24hPct) : 'text.secondary';

                  const lastInventoryUpdate = lastInventoryUpdateTimestamps[character.id];
                  return (
                    <TableRow
                      key={character.id}
                      onClick={() => handleCharacterClick(character)}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' },
                        '& td': { whiteSpace: 'nowrap', height: 52, overflow: 'hidden' },
                      }}
                    >
                      <TableCell padding='checkbox' onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(character.id)}
                          onChange={() => toggleRow(character.id)}
                          inputProps={{ 'aria-label': `select character ${character.name}` }}
                        />
                      </TableCell>
                      <TableCell component='th' scope='row' sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {(() => {
                              const lastAliveIso = aliveStatuses[character.id];
                              const isAlive = lastAliveIso && Date.now() - new Date(lastAliveIso).getTime() <= 2 * 60_000;
                              return (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: isAlive ? '#4caf50' : '#f44336',
                                    mr: 1,
                                    flexShrink: 0,
                                  }}
                                />
                              );
                            })()}
                            <Typography variant='body1'>{character.name}</Typography>
                          </Box>
                          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                            {character.gameAccount ? character.gameAccount.accountName : 'Unknown Account'}
                          </Typography>
                          <Typography variant='body2' color='text.secondary' sx={{ display: 'block' }}>
                            Hostname: {character.hostname || 'Unknown'}
                          </Typography>
                          {(() => {
                            const lastAliveIso = aliveStatuses[character.id];
                            const isAlive = lastAliveIso && Date.now() - new Date(lastAliveIso).getTime() <= 2 * 60_000;

                            if (!isAlive && lastAliveIso) {
                              return (
                                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                  Last Alive: {formatTimeAgo(lastAliveIso)} ago
                                </Typography>
                              );
                            } else if (!isAlive && !lastAliveIso) {
                              return (
                                <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                  Last Alive: Never
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', width: 65, borderRight: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                        <Box>
                          <Typography variant='body2'>
                            <Box component='span' sx={{ color: 'text.secondary', opacity: 0.7 }}>L: </Box>
                            <Box component='span' sx={{ fontWeight: 'bold' }}>{character.level}</Box>
                          </Typography>
                          <Typography variant='body2'>
                            <Box component='span' sx={{ color: 'text.secondary', opacity: 0.7 }}>D: </Box>
                            <Box component='span'>{character.deathsPerHour != null ? character.deathsPerHour.toFixed(2) : 'N/A'}/hr</Box>
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', width: 130, borderRight: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                              {character.divineOrbCount ?? 0}
                            </Typography>
                            {divPerHourArrow && (
                              <Typography variant='body2' sx={{ color: divPerHourColor }}>
                                {divPerHourArrow}
                              </Typography>
                            )}
                            <Typography variant='body2' sx={{ color: divPerHourColor }}>
                              {perHourData ? `${divPerHour.toFixed(2)}/hr` : 'N/A'}
                            </Typography>
                          </Box>
                          <Typography variant='body2' sx={{ color: divsGained24hColor }}>
                            {gained24hData ? `24h: ${divsGained24h >= 0 ? '+' : ''}${Math.round(divsGained24h)}` : '24h: N/A'}
                          </Typography>
                          {(() => {
                            const history = divineOrbHistory[character.id];
                            if (history && history.length > 0) {
                              const chartData = history.map(h => ({ q: h.quantity }));
                              return (
                                <LineChart width={50} height={18} data={chartData}>
                                  <Line type='monotone' dataKey='q' stroke={divsGained24hColor} dot={false} strokeWidth={1.5} />
                                </LineChart>
                              );
                            }
                            return null;
                          })()}
                          <Typography variant='body2' color='text.secondary'>
                            {lastInventoryUpdate ? `Updated: ${formatTimeAgo(lastInventoryUpdate)}` : 'Updated: Never'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        {character.build || character.stage ? (
                          <Box>
                            {character.build && <Typography variant='body2'>{character.build}</Typography>}
                            {character.stage && (
                              <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                                {character.stage}
                              </Typography>
                            )}
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()} sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                            <Chip
                              label={(() => {
                                const { baseState } = parseState(character.state);
                                return baseState || 'Unknown';
                              })()}
                              size='small'
                              sx={{
                                color: getStateColorConfig(character.state).color,
                                bgcolor: getStateColorConfig(character.state).bgColor,
                                border: `1px solid ${getStateColorConfig(character.state).color}`,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 24,
                                maxWidth: 'fit-content',
                              }}
                            />
                            {(() => {
                              const parsed = parseState(character.state);
                              const atlasPoints = character.atlasPoints ?? parsed.atlasPoints;
                              const build = character.build ?? parsed.build;
                              const parts: string[] = [];
                              if (atlasPoints !== null) parts.push(`AP ${atlasPoints}`);
                              if (build !== null) parts.push(build);
                              if (parts.length) {
                                return (
                                  <Typography variant='caption' sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1 }}>
                                    {parts.join(' \u2022 ')}
                                  </Typography>
                                );
                              }
                              return null;
                            })()}
                          </Box>
                          {userFeatures.includes('Boosting State') && (
                            <FormControl size='small' sx={{ minWidth: 140 }}>
                              <Select
                                value={character.boostingState ?? 0}
                                onChange={e => handleBoostingStateChange(character.id, Number(e.target.value))}
                                displayEmpty
                                sx={{ fontSize: '0.75rem' }}
                              >
                                <MenuItem value={0}>Active</MenuItem>
                                <MenuItem value={1}>Boosted by Operator</MenuItem>
                                <MenuItem value={2}>External Booster</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()} sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: 250, minWidth: 150 }}>
                          {editingNote[character.id] ? (
                            <>
                              <TextField
                                size='small'
                                value={noteValues[character.id] || ''}
                                onChange={e => handleNoteChange(character.id, e.target.value)}
                                placeholder='Add a note...'
                                sx={{ flexGrow: 1, minWidth: 100 }}
                              />
                              <IconButton
                                size='small'
                                onClick={() => handleSaveNote(character.id)}
                                disabled={savingNote[character.id]}
                                color='primary'
                              >
                                {savingNote[character.id] ? <CircularProgress size={16} /> : <SaveIcon />}
                              </IconButton>
                              <IconButton size='small' onClick={() => handleCancelEditNote(character.id)}>
                                <CancelIcon />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <Typography
                                variant='body2'
                                sx={{
                                  flexGrow: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                }}
                                onClick={() => handleEditNote(character.id, character.note || '')}
                              >
                                {character.note || 'Click to add note...'}
                              </Typography>
                              <IconButton
                                size='small'
                                onClick={() => handleEditNote(character.id, character.note || '')}
                                sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                              >
                                <EditIcon fontSize='small' />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align='right' onClick={e => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title='Copy Character Info'>
                            <IconButton color='primary' size='small' onClick={e => handleCopyCharacterInfo(e, character)}>
                              <ContentCopyIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Stop in Hideout'>
                            <IconButton
                              color='error'
                              size='small'
                              disabled={stopping[character.id]}
                              onClick={e => handleStopInHideout(e, character.id)}
                            >
                              {stopping[character.id] ? <CircularProgress size={16} /> : <StopIcon />}
                            </IconButton>
                          </Tooltip>
                          {userFeatures.includes('RMT') && (
                            <Tooltip title='Withdraw Divine Orbs'>
                              <IconButton color='success' size='small' onClick={e => handleRmtClick(e, character.id)}>
                                <AttachMoneyIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </EnhancedCard>
      );
    }
  } else if (status === 'failed') {
    let errorMessage = 'Failed to load characters.';
    if (error) {
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (typeof error === 'object' && error !== null && 'message' in error) {
        errorMessage = String((error as { message: string }).message);
      } else {
        // Fallback for other types of error objects or if message is not present
        errorMessage = 'An unexpected error occurred.';
        console.error('Unknown error format:', error);
      }
    }
    content = <Alert severity='error'>Error: {errorMessage}</Alert>;
  }

  // Handlers for game and league changes
  const handleGameChange = (gameCode: string) => {
    console.log(`Game changed to: ${gameCode}`);
    // The actual filtering will happen via the useEffect dependency on selectedGame
  };

  const handleLeagueChange = (leagueCode: string) => {
    console.log(`League changed to: ${leagueCode}`);
    // The actual filtering will happen via the useEffect dependency on selectedLeague
  };

  return (
    <PageLayout>
      <PageHeader
        icon={<PeopleIcon />}
        title='Character Management'
        subtitle='Monitor and manage your bot characters'
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isPeriodicRefreshing && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CircularProgress size={16} />
                <Typography variant='caption' color='text.secondary'>
                  Refreshing data...
                </Typography>
              </Box>
            )}
            <Button
              variant='outlined'
              color='primary'
              onClick={() => setBulkEditOpen(true)}
              disabled={selectedIds.size === 0}
              startIcon={<EditNoteIcon />}
            >
              Bulk edit note ({selectedIds.size})
            </Button>
            {isOwnerOrAdmin && (
              <Button variant='outlined' color='secondary' onClick={() => setManageTemplatesOpen(true)} startIcon={<LibraryBooksIcon />}>
                Manage templates
              </Button>
            )}
            <Button
              variant='outlined'
              color='error'
              onClick={handleDeleteAllClick}
              disabled={characters.length === 0 || isBulkDeleting}
              startIcon={<DeleteSweepIcon />}
            >
              Delete All ({characters.length})
            </Button>
            <Button variant='contained' color='error' onClick={handleReloadAllScripts} disabled={reloadingAll || characters.length === 0}>
              {reloadingAll ? <CircularProgress size={20} color='inherit' /> : 'Reload all scripts'}
            </Button>
          </Box>
        }
      />

      <ContentSection>
        <GameLeagueSelector onGameChange={handleGameChange} onLeagueChange={handleLeagueChange} />
      </ContentSection>

      <ContentSection>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab label='Characters' />
          <Tab label='Analysis' />
        </Tabs>
      </ContentSection>

      {activeTab === 0 && (
        <>
          <ContentSection>
            <CharacterStatsDashboard characters={characters} />
          </ContentSection>

          {content}
        </>
      )}

      {activeTab === 1 && (
        <ContentSection>
          <BuildAnalysisView characters={characters} divineOrbsPerHour={divineOrbsPerHour} divineOrbsGained24h={divineOrbsGained24h} />
        </ContentSection>
      )}

      {/* Character information panel */}
      <CharacterPanel
        open={isPanelOpen}
        onClose={handleClosePanel}
        character={selectedCharacter}
        loading={characterItemsStatus === 'loading'}
        items={characterItems}
        divineOrbHistory={selectedCharacter ? divineOrbHistory[selectedCharacter.id] : undefined}
        divineOrbsPerHour={selectedCharacter ? divineOrbsPerHour[selectedCharacter.id] : undefined}
      />

      {/* Bulk delete confirmation dialog */}
      <BulkDeleteConfirmationDialog
        open={bulkDeleteDialogOpen}
        onClose={handleBulkDeleteCancel}
        onConfirm={handleBulkDeleteConfirm}
        characters={characters}
        isDeleting={isBulkDeleting}
      />

      <BulkEditNoteModal
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        selectedIds={Array.from(selectedIds)}
        onApplied={({ updated }) => {
          setBulkEditOpen(false);
          clearSelection();
          void dispatch(fetchCharacters());
          dispatch(showNotification({ message: `Updated note on ${updated} character(s).`, type: 'success' }));
        }}
      />
      <NoteTemplatesAdminModal open={manageTemplatesOpen} onClose={() => setManageTemplatesOpen(false)} />

      {/* RMT Withdrawal Dialog */}
      <Dialog open={rmtModalOpen} onClose={handleRmtClose} maxWidth='sm' fullWidth>
        <DialogTitle>Withdraw Divine Orbs</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {rmtCharacterId &&
              (() => {
                const character = characters.find(c => c.id === rmtCharacterId);
                return character ? (
                  <>
                    <Typography variant='body1' sx={{ mb: 2 }}>
                      Character: <strong>{character.name}</strong>
                    </Typography>
                    <Typography variant='body2' sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      Current Balance: <DivineOrbIcon size={16} />
                      <strong>{character.divineOrbCount ?? 0}</strong> div
                    </Typography>
                    <TextField
                      autoFocus
                      margin='dense'
                      label='Divine Orb Amount'
                      type='number'
                      fullWidth
                      variant='outlined'
                      value={rmtDivAmount}
                      onChange={e => setRmtDivAmount(e.target.value)}
                      error={!!rmtError}
                      helperText={rmtError}
                      disabled={rmtSubmitting}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </>
                ) : null;
              })()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRmtClose} disabled={rmtSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleRmtSubmit} variant='contained' color='success' disabled={rmtSubmitting || !rmtDivAmount}>
            {rmtSubmitting ? <CircularProgress size={20} color='inherit' /> : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default CharacterManagementPage;
